import { useQuery } from "@apollo/client";
import React, { FC } from "react";
import Leader from "./Leader";
import { ONBOARDING } from "../../graphql/onboarding/onboarding/query";
import { COMPANY_INFO_PAPPERS } from "../../graphql/onboarding/papers/query";
import { OnboardingInfo } from "../../graphql/onboarding/onboarding/types/OnboardingInfo";
import {
  FetchCompanyPappers,
  FetchCompanyPappersVariables,
} from "../../graphql/onboarding/papers/types/FetchCompanyPappers";
import { DEFAULT_COUNTRY_CODE } from "../../constants/countries";
import {
  CreateMemberInput,
  CreateUserInput,
} from "../../types/graphql-global-types-onboarding";
import { SelectItemInterface } from "../../interface/SelectItemInterface";
import StatusGetID from "../GetId/statusGetId";
import Layout from "../Common/NewLayout";
import Header from "../Common/NewLayout/Header";
import { useToastNotifications } from "../../utils/toastUtils";
import {
  DocumentsType,
  DocumentsTypeVariables,
} from "../../graphql/onboarding/document/types/DocumentsType";
import { DOCUMENTS_TYPE } from "../../graphql/onboarding/document/query";
import { DocumentType } from "../../enums/document";

const documentTypes = [
  DocumentType.IDENTITY_CARD,
  DocumentType.PASSPORT,
  DocumentType.DRIVING_LICENCE,
  DocumentType.PROOF_OF_ADDRESS,
];

const defaultOwner: CreateUserInput = {
  id: "",
  address: "",
  city: "",
  cityOfBirth: "",
  countryOfBirthIsoCode: "",
  dateOfBirth: "",
  firstname: "",
  name: "",
  gender: "",
  isLegalRepresentative: true,
  nationalityCountryIsoCode: "FR",
  countryIsoCode: "FR",
  sharesPercentage: null,
  postcode: "",
  type: "LEADER",
  specifiedUSPerson: null,
  documents: [],
  isDirector: false,
  directorAppointmentDate: null,
};

const LeaderProvider: FC = () => {
  const personItems: SelectItemInterface[] = [];
  const representativesList: CreateUserInput[] = [];
  let legalRepresentative = { ...defaultOwner };

  const { data: companyPappers, loading: pappersLoading } = useQuery<
    FetchCompanyPappers,
    FetchCompanyPappersVariables
  >(COMPANY_INFO_PAPPERS, { fetchPolicy: "no-cache" });

  const { data: typesDocument, loading: loadingDocumentsType } = useQuery<
    DocumentsType,
    DocumentsTypeVariables
  >(DOCUMENTS_TYPE, {
    fetchPolicy: "network-only",
    variables: { types: documentTypes },
  });

  const { active, loading: loadingGetId } = StatusGetID();
  const { showErrorToast } = useToastNotifications();
  const { data: onboarding, loading } = useQuery<OnboardingInfo>(ONBOARDING, {
    fetchPolicy: "network-only",
    onError: () => {
      showErrorToast({
        description: `onboarding.retrieving.information.error`,
      });
    },
  });

  if (onboarding?.onboardingInfo?.legalRepresentative?.id) {
    Object.assign(
      legalRepresentative,
      onboarding.onboardingInfo.legalRepresentative,
    );
  }

  const corporateInfo = onboarding?.onboardingInfo?.company;
  if (corporateInfo?.isAutocomplete && companyPappers?.fetchCompanyPappers) {
    const { representatives } = companyPappers.fetchCompanyPappers;
    const ownerId = `${legalRepresentative?.firstname || ""} ${
      legalRepresentative?.name || ""
    }`;
    const isNew = !representatives?.some(
      (p) => `${p?.firstname || ""} ${p?.name || ""}` === ownerId,
    );
    const hasNoOwnerId = !onboarding?.onboardingInfo?.legalRepresentative?.id;

    if (representatives?.length && (hasNoOwnerId || isNew)) {
      if (representatives.length === 1) {
        Object.assign(legalRepresentative, representatives[0]);
      } else {
        representatives.forEach((p) => {
          if (p) {
            personItems.push({
              id: `${p.firstname || ""} ${p.name || ""} ${
                p.nationalityCountryIsoCode || ""
              } ${p.city || ""} ${p.address || ""} ${p.countryIsoCode || ""} ${
                p.postcode || ""
              }`,
              label: `${p.firstname || ""} ${p.name}`,
            });
            representativesList.push(p as any);
          }
        });
        legalRepresentative = { ...defaultOwner };
      }
    }
  }

  if (onboarding?.onboardingInfo?.legalRepresentative?.documents?.length) {
    legalRepresentative.documents =
      onboarding.onboardingInfo.legalRepresentative.documents
        .filter(
          (d) =>
            d?.type?.type !== undefined &&
            d?.type?.type !== null &&
            documentTypes.includes(d.type.type as DocumentType),
        )
        .map((d) => ({
          filename: d?.filename,
          fileType: d?.fileType,
          base64Content: d?.base64Content,
          type: d?.type?.type as DocumentType,
        }));
  }

  return (
    <Layout
      title="Dirigeants"
      description="Renseignez les informations relatives au représentant légal de votre entreprise."
      isDoubleContent
      loading={
        loading || pappersLoading || loadingGetId || loadingDocumentsType
      }
    >
      <Header
        title="Informations Représentant Légal"
        description="Indiquez les informations du représentant légal."
      />
      <Leader
        owner={legalRepresentative}
        next={
          corporateInfo?.isIndividual
            ? active
              ? "identity-verification"
              : "contrat"
            : "/shareholdersPercent"
        }
        previous={`/companyInfo/${
          corporateInfo?.isAutocomplete ? corporateInfo?.code || "" : "create"
        }`}
        isIndividual={!!corporateInfo?.isIndividual}
        personItems={personItems}
        representativesList={representativesList}
        documentsType={typesDocument?.documentsType ?? []}
      />
    </Layout>
  );
};

export default LeaderProvider;
